<template>
  <div>
    <div class="list-header">
      <div class="list-title">{{ $t(title) }}</div>
      <tol-type-ahead
        class="list-search"
        :search-params="{
          url: '/projects',
          searchKeys: ['searchParam'],
          urlParams
        }"
        :icon-config="{ label: 'search' }"
        :results-in-dropdown="false"
        :no-clear-search="true"
        @filtered="onFiltered($event)"
      >
      </tol-type-ahead>
      <router-link to="/projects/create">
        <div class="create-project-container">
          <TolAltButton label="Create Project" />
        </div>
      </router-link>
    </div>
    <tol-table
      :table-data="projects"
      :columns="columns"
      :show-spinner="isLoading"
      :is-row-clickable="true"
      @row-click="onRowClick($event)"
    >
      <template v-slot:tableHeader>
        <th-sortable
          :direction="currentSortField === 'client_name' ? currentSortDirection : 'none'"
          field-name="client_name"
          @change="onSortOrderChange"
        >
          {{ $t('projects.listing.labels.client_name') }}
        </th-sortable>
        <th-sortable
          :direction="currentSortField === 'end_client_name' ? currentSortDirection : 'none'"
          field-name="end_client_name"
          @change="onSortOrderChange"
        >
          {{ $t('projects.listing.labels.end_client_name') }}
        </th-sortable>
        <th-sortable
          :direction="currentSortField === 'name' ? currentSortDirection : 'none'"
          field-name="name"
          @change="onSortOrderChange"
        >
          {{ $t('projects.listing.labels.name') }}
        </th-sortable>
        <th-sortable
          :direction="currentSortField === 'start_date' ? currentSortDirection : 'none'"
          field-name="start_date"
          @change="onSortOrderChange"
        >
          {{ $t('projects.listing.labels.start_date') }}
        </th-sortable>
        <th-sortable
          :direction="currentSortField === 'end_date' ? currentSortDirection : 'none'"
          field-name="end_date"
          @change="onSortOrderChange"
        >
          {{ $t('projects.listing.labels.end_date') }}
        </th-sortable>
        <th-sortable
          :direction="currentSortField === 'status' ? currentSortDirection : 'none'"
          field-name="status"
          @change="onSortOrderChange"
        >
          {{ $t('projects.listing.labels.status') }}
        </th-sortable>
      </template>
      <template v-slot:updated_dt="{ value }">
        {{ formatDateWithTime(value.updated_dt) }}
      </template>
    </tol-table>
    <Pagination
      :records-per-page="listPerPage"
      :offset="currentPage"
      :record-count="recordCount"
      @change="onChangePaginationParams($event)"
    />
  </div>
</template>

<script>
import format from 'date-fns/format';
import Pagination from '@/app/components/Pagination';
import ThSortable from '@/app/components/ThSortable';
import TolAltButton from '@/app/components/TolAltButton';
import TolTable from '@/app/components/TolTable';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import { AppActions } from '@/app/app.keys';
import { ProjectsListGetters, ProjectsListActions } from './projects-list.keys';

export default {
  name: 'ProjectsList',
  components: {
    TolAltButton,
    TolTypeAhead,
    TolTable,
    ThSortable,
    Pagination
  },
  data() {
    return {
      isBeingFiltered: false
    };
  },
  computed: {
    title() {
      return 'projects.listing.titleAll';
    },
    isLoading() {
      return this.$store.getters[ProjectsListGetters.ProjectsIsLoading] || this.isBeingFiltered;
    },
    projects() {
      return this.$store.getters[ProjectsListGetters.Projects];
    },
    columns() {
      return ['client_name', 'end_client_name', 'name', 'start_date', 'end_date', 'status'];
    },
    currentSortField() {
      return this.$store.getters[ProjectsListGetters.ProjectsSortBy];
    },
    currentSortDirection() {
      return this.$store.getters[ProjectsListGetters.ProjectsSortDirection];
    },
    listPerPage() {
      return this.$store.getters[ProjectsListGetters.ProjectsPerPage];
    },
    currentPage() {
      return this.$store.getters[ProjectsListGetters.ProjectsOffset];
    },
    recordCount() {
      return this.$store.getters[ProjectsListGetters.ProjectsCount];
    },
    tolAlert() {
      return false;
    },
    sortParams() {
      return this.$store.getters[ProjectsListGetters.ProjectsSortOrder];
    },
    paginationParams() {
      return this.$store.getters[ProjectsListGetters.ProjectsPaginationParams];
    },
    urlParams() {
      return {
        ...this.sortParams,
        ...this.paginationParams
      };
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('projects.listing.title'));
    this.$store.dispatch(ProjectsListActions.GetProjects);
  },
  methods: {
    onSortOrderChange(sortOrder) {
      this.$store.dispatch(ProjectsListActions.SetProjectsSortOrder, sortOrder);
    },
    async onFiltered(event) {
      this.isBeingFiltered = true;
      await this.$store.dispatch(ProjectsListActions.FilterListBySearchKey, {
        filteredItems: event.data,
        isSearchKey: event.isSearchKey,
        searchParam: event.searchParam
      });
      this.isBeingFiltered = false;
    },
    onChangePaginationParams(event) {
      this.$store.dispatch(ProjectsListActions.SetProjectsPaginationParameters, event);
    },
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },
    formatDateWithTime(d) {
      return d ? format(d, 'YYYY-MM-DD HH:mm') : null;
    },
    onRowClick(event) {
      this.$router.push(`/projects/${event.reference_number}`);
    }
  }
};
</script>

<style lang="scss">
@import '@/app/styles/colors.scss';

.list-header {
  display: flex;
  align-items: center;
  background-color: $background-level-2;
  padding: 60px;

  .list-title {
    margin-right: 1rem;
    color: $text-color-dark;
    font-size: 24px;
    font-weight: bold;
    letter-spacing: -0.24px;
    line-height: 28px;
  }

  .list-search {
    flex-grow: 1;
  }
}

.create-project-container {
  display: inline-block;
  padding: 0 1rem;
  text-decoration: none;
  color: $text-color-dark;
  white-space: nowrap;
}
</style>
